import React, { FC, useEffect, useState } from "react";
import { Cta } from "@xapp/stentor-chat-widget";

import "./ActionBarCta.scss";

export interface ActionBarCtaProps {
    /**
     * CTA configuration
     */
    readonly config: Cta;
    /**
     * Whether we're on mobile (affects styling)
     */
    readonly isMobile: boolean;
    /**
     * Called when the CTA is clicked (opens chat)
     */
    readonly onClick: () => void;
    /**
     * Called when the CTA is dismissed
     */
    readonly onDismiss: () => void;
    /**
     * Whether the user has already interacted (clicked chat, dismissed CTA, etc.)
     */
    readonly hasUserInteracted: boolean;
}

/**
 * CTA banner for ActionBar - a full-width rectangular banner that appears
 * above the action bar to prompt users to engage with chat.
 */
const ActionBarCta: FC<ActionBarCtaProps> = ({
    config,
    isMobile,
    onClick,
    onDismiss,
    hasUserInteracted,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    // For mobile overrides: null means "explicitly disabled", undefined means "use desktop default"
    const mobileMessage = isMobile ? config.mobile?.message : undefined;
    const message = mobileMessage !== undefined ? mobileMessage : config.message;
    const delay = isMobile && config.mobile?.delay !== undefined ? config.mobile.delay : (config.delay ?? 0);
    const mobileTimeout = isMobile ? config.mobile?.timeout : undefined;
    const timeout = mobileTimeout !== undefined ? mobileTimeout : config.timeout;
    const mobileAnimation = isMobile ? config.mobile?.animation : undefined;
    const animation = mobileAnimation !== undefined ? mobileAnimation : config.animation;
    const animationDelay = isMobile && config.mobile?.animationDelay !== undefined
        ? config.mobile.animationDelay
        : (config.animationDelay ?? delay);
    const animationTimeout = isMobile && config.mobile?.animationTimeout !== undefined
        ? config.mobile.animationTimeout
        : config.animationTimeout;
    const dismissible = isMobile && config.mobile?.dismissible !== undefined
        ? config.mobile.dismissible
        : (config.dismissible ?? true);

    // Show CTA after delay
    useEffect(() => {
        if (!message || hasUserInteracted || isDismissed) {
            setIsVisible(false);
            return undefined;
        }

        const showTimer = setTimeout(() => {
            setIsVisible(true);
        }, delay);

        return () => clearTimeout(showTimer);
    }, [message, delay, hasUserInteracted, isDismissed]);

    // Hide CTA after timeout
    useEffect(() => {
        if (!isVisible || !timeout) {
            return undefined;
        }

        const hideTimer = setTimeout(() => {
            setIsVisible(false);
        }, timeout);

        return () => clearTimeout(hideTimer);
    }, [isVisible, timeout]);

    // Start animation after animationDelay
    useEffect(() => {
        if (!animation || hasUserInteracted || isDismissed) {
            setIsAnimating(false);
            return undefined;
        }

        const animateTimer = setTimeout(() => {
            setIsAnimating(true);
        }, animationDelay);

        return () => clearTimeout(animateTimer);
    }, [animation, animationDelay, hasUserInteracted, isDismissed]);

    // Stop animation after animationTimeout
    useEffect(() => {
        if (!isAnimating || !animationTimeout) {
            return undefined;
        }

        const stopTimer = setTimeout(() => {
            setIsAnimating(false);
        }, animationTimeout);

        return () => clearTimeout(stopTimer);
    }, [isAnimating, animationTimeout]);

    const handleClick = () => {
        onClick();
        setIsVisible(false);
    };

    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDismissed(true);
        setIsVisible(false);
        onDismiss();
    };

    if (!isVisible || !message) {
        return null;
    }

    const animationClass = isAnimating && animation ? `xapp-action-bar-cta--${animation}` : "";

    return (
        <div
            className={`xapp-action-bar-cta ${animationClass}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={message}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <span className="xapp-action-bar-cta__message">{message}</span>
            {dismissible && (
                <button
                    className="xapp-action-bar-cta__dismiss"
                    onClick={handleDismiss}
                    aria-label="Dismiss"
                    type="button"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M13 1L1 13M1 1L13 13"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default ActionBarCta;
